import type { SupportedLanguage } from '../i18n/routes'

export type VehicleText = Record<SupportedLanguage, string>
export type VehiclePublication = 'draft' | 'published' | 'archived'
export type VehicleAvailability = 'available' | 'reserved' | 'sold'
export type VehicleEquipment = {
  id: string
  category: 'comfort' | 'safety' | 'technology' | 'exterior' | 'other'
  kind: 'standard' | 'optional'
  label: VehicleText
}
export type VehicleDocument = { id: string; title: VehicleText; url: string }
export type VehicleFinance = {
  monthlyEuro: number
  depositEuro: number
  durationMonths: number
  financedEuro?: number
  finalPaymentEuro?: number
  tanPercent?: number
  taegPercent?: number
  feesEuro?: number
  totalDueEuro?: number
  terms?: VehicleText
}

/** Public fields only. VIN, plates and internal notes belong to the future server-side admin model. */
export type VehicleSpecifications = {
  condition?: VehicleText
  registration?: VehicleText
  stockId?: string
  body?: VehicleText
  exteriorColor?: VehicleText
  interiorColor?: VehicleText
  upholstery?: VehicleText
  doors?: number
  seats?: number
  owners?: number
  serviceHistory?: VehicleText
  lastService?: VehicleText
  inspectionDue?: VehicleText
  warrantyMonths?: number
  warrantyTerms?: VehicleText
  newDriverEligible?: boolean
  accidentFree?: boolean
  displacementCc?: number
  powerKw?: number
  powerHp?: number
  engineType?: VehicleText
  gears?: number
  drive?: VehicleText
  torqueNm?: number
  torqueRpm?: number
  powerRpm?: number
  topSpeedKmh?: number
  accelerationSeconds?: number
  lengthCm?: number
  widthCm?: number
  heightCm?: number
  wheelbaseCm?: number
  bootLitres?: number
  weightKg?: number
  towingKg?: number
  emissionClass?: string
  co2GKm?: number
  urbanL100Km?: number
  extraUrbanL100Km?: number
  combinedL100Km?: number
  measurementCycle?: VehicleText
  batteryKwh?: number
  rangeKm?: number
  consumptionKwh100Km?: number
  chargingKw?: number
  chargingHours?: number
  vatDeductible?: boolean
  transferIncluded?: boolean
  delivery?: VehicleText
}

type Field = {
  key: keyof VehicleSpecifications
  label: VehicleText
  unit?: string
}
export const vehicleSpecificationSections: readonly { id: string; title: VehicleText; fields: readonly Field[] }[] = [
  { id: 'identity', title: { it: 'Identità e condizioni', en: 'Identity and condition' }, fields: [
    { key: 'stockId', label: { it: 'Codice stock', en: 'Stock ID' } },
    { key: 'condition', label: { it: 'Condizione', en: 'Condition' } },
    { key: 'registration', label: { it: 'Immatricolazione', en: 'First registration' } },
    { key: 'owners', label: { it: 'Precedenti proprietari', en: 'Previous owners' } },
    { key: 'newDriverEligible', label: { it: 'Per neopatentati', en: 'Suitable for new drivers' } },
    { key: 'accidentFree', label: { it: 'Non incidentata', en: 'Accident-free' } },
    { key: 'serviceHistory', label: { it: 'Storico manutenzione', en: 'Service history' } },
    { key: 'lastService', label: { it: 'Ultimo tagliando', en: 'Last service' } },
    { key: 'inspectionDue', label: { it: 'Scadenza revisione', en: 'Inspection due' } },
    { key: 'warrantyMonths', label: { it: 'Garanzia (mesi)', en: 'Warranty (months)' } },
    { key: 'warrantyTerms', label: { it: 'Condizioni di garanzia', en: 'Warranty terms' } },
  ] },
  { id: 'engine', title: { it: 'Motore e prestazioni', en: 'Engine and performance' }, fields: [
    { key: 'displacementCc', label: { it: 'Cilindrata', en: 'Displacement' }, unit: 'cm³' },
    { key: 'powerKw', label: { it: 'Potenza', en: 'Power' }, unit: 'kW' },
    { key: 'powerHp', label: { it: 'Potenza (CV)', en: 'Power (PS)' } },
    { key: 'engineType', label: { it: 'Tipo motore', en: 'Engine type' } },
    { key: 'gears', label: { it: 'Numero marce', en: 'Gears' } },
    { key: 'drive', label: { it: 'Trazione', en: 'Drivetrain' } },
    { key: 'torqueNm', label: { it: 'Coppia massima', en: 'Maximum torque' }, unit: 'Nm' },
    { key: 'torqueRpm', label: { it: 'Regime coppia', en: 'Torque speed' }, unit: 'rpm' },
    { key: 'powerRpm', label: { it: 'Regime potenza', en: 'Power speed' }, unit: 'rpm' },
    { key: 'topSpeedKmh', label: { it: 'Velocità massima', en: 'Top speed' }, unit: 'km/h' },
    { key: 'accelerationSeconds', label: { it: 'Accelerazione 0–100 km/h', en: 'Acceleration 0–100 km/h' }, unit: 's' },
  ] },
  { id: 'body', title: { it: 'Carrozzeria e dimensioni', en: 'Body and dimensions' }, fields: [
    { key: 'body', label: { it: 'Carrozzeria', en: 'Body style' } },
    { key: 'exteriorColor', label: { it: 'Colore esterno', en: 'Exterior colour' } },
    { key: 'interiorColor', label: { it: 'Colore interno', en: 'Interior colour' } },
    { key: 'upholstery', label: { it: 'Rivestimenti', en: 'Upholstery' } },
    { key: 'doors', label: { it: 'Porte', en: 'Doors' } },
    { key: 'seats', label: { it: 'Posti', en: 'Seats' } },
    { key: 'lengthCm', label: { it: 'Lunghezza', en: 'Length' }, unit: 'cm' },
    { key: 'widthCm', label: { it: 'Larghezza', en: 'Width' }, unit: 'cm' },
    { key: 'heightCm', label: { it: 'Altezza', en: 'Height' }, unit: 'cm' },
    { key: 'wheelbaseCm', label: { it: 'Passo', en: 'Wheelbase' }, unit: 'cm' },
    { key: 'bootLitres', label: { it: 'Bagagliaio', en: 'Boot capacity' }, unit: 'L' },
    { key: 'weightKg', label: { it: 'Peso', en: 'Weight' }, unit: 'kg' },
    { key: 'towingKg', label: { it: 'Massa rimorchiabile', en: 'Towing capacity' }, unit: 'kg' },
  ] },
  { id: 'efficiency', title: { it: 'Consumi ed emissioni', en: 'Consumption and emissions' }, fields: [
    { key: 'emissionClass', label: { it: 'Classe emissioni', en: 'Emission class' } },
    { key: 'co2GKm', label: { it: 'Emissioni CO₂', en: 'CO₂ emissions' }, unit: 'g/km' },
    { key: 'urbanL100Km', label: { it: 'Consumo urbano', en: 'Urban consumption' }, unit: 'L/100 km' },
    { key: 'extraUrbanL100Km', label: { it: 'Consumo extraurbano', en: 'Extra-urban consumption' }, unit: 'L/100 km' },
    { key: 'combinedL100Km', label: { it: 'Consumo misto', en: 'Combined consumption' }, unit: 'L/100 km' },
    { key: 'measurementCycle', label: { it: 'Ciclo di omologazione', en: 'Test cycle' } },
    { key: 'batteryKwh', label: { it: 'Capacità batteria', en: 'Battery capacity' }, unit: 'kWh' },
    { key: 'rangeKm', label: { it: 'Autonomia elettrica', en: 'Electric range' }, unit: 'km' },
    { key: 'consumptionKwh100Km', label: { it: 'Consumo elettrico', en: 'Electric consumption' }, unit: 'kWh/100 km' },
    { key: 'chargingKw', label: { it: 'Potenza di ricarica', en: 'Charging power' }, unit: 'kW' },
    { key: 'chargingHours', label: { it: 'Tempo di ricarica', en: 'Charging time' }, unit: 'h' },
  ] },
  { id: 'terms', title: { it: 'Condizioni commerciali', en: 'Commercial terms' }, fields: [
    { key: 'vatDeductible', label: { it: 'IVA esposta', en: 'VAT itemised' } },
    { key: 'transferIncluded', label: { it: 'Immatricolazione e passaggio inclusi', en: 'Registration and transfer fees included' } },
    { key: 'delivery', label: { it: 'Consegna', en: 'Delivery' } },
  ] },
]
